import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircle, Home, Phone, Mail, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast, Toaster } from '../components/ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSuccess = () => {  
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(location.state?.user || null);
  const [calendarAdded, setCalendarAdded] = useState(false);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setLoading(false);
    }

    // Check for user in location state (from auth callback)
    if (location.state?.addToCalendar && location.state?.bookingData) {
      handleAddToGoogleCalendar(location.state.bookingData);
    }
  }, [sessionId, location.state]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/booking/payment-status/${sessionId}`);
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Error checking payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToGoogleCalendar = (bookingData) => {
    // Create Google Calendar event URL
    const eventTitle = `JUNKio Junk Removal Service`;
    const eventDetails = bookingData ? 
      `Package: ${bookingData.package_name || 'Junk Removal'}\nAddress: ${bookingData.address || 'TBD'}\nBooking ID: ${bookingData.booking_id || paymentStatus?.booking_id}` :
      `Booking ID: ${paymentStatus?.booking_id}`;
    
    const serviceDate = bookingData?.service_date || new Date().toISOString().split('T')[0];
    const startDate = serviceDate.replace(/-/g, '');
    const endDate = startDate; // Same day event
    
    // Google Calendar URL format
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(eventDetails)}&location=${encodeURIComponent(bookingData?.address || 'Hamilton, ON')}`;
    
    window.open(calendarUrl, '_blank');
    setCalendarAdded(true);
    toast.success('Opening Google Calendar to add your event');
  };

  const initiateGoogleLogin = () => {
    // Store booking data for after auth
    if (paymentStatus) {
      sessionStorage.setItem('pendingCalendarBooking', JSON.stringify({
        booking_id: paymentStatus.booking_id,
        service_date: paymentStatus.service_date,
        package_name: paymentStatus.package_name,
        address: paymentStatus.address
      }));
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/booking-success';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const addToCalendarDirect = () => {
    // Direct Google Calendar add (without login)
    handleAddToGoogleCalendar({
      booking_id: paymentStatus?.booking_id,
      service_date: new Date().toISOString().split('T')[0],
      package_name: 'Junk Removal Service',
      address: 'Hamilton, ON'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600">Confirming your booking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white py-12 px-4">
      <Toaster />
      <div className="max-w-2xl mx-auto">
        <Card className="p-8 text-center">
          <div className="mb-6">
            <CheckCircle className="w-20 h-20 text-emerald-600 mx-auto mb-4 animate-bounce" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Booking Confirmed!
            </h1>
            <p className="text-lg text-gray-600">
              Thank you for choosing JUNKio - Junk Removal
            </p>
          </div>

          {paymentStatus && (
            <div className="bg-emerald-50 rounded-lg p-6 mb-6 text-left">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Booking Details</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Booking ID:</span>
                  <span className="font-semibold text-gray-900">{paymentStatus.booking_id}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span className="font-semibold text-emerald-600 uppercase">
                    {paymentStatus.payment_status}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-semibold text-gray-900">
                    ${paymentStatus.amount?.toFixed(2)} {paymentStatus.currency?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Add to Google Calendar Button */}
          <div className="mb-6">
            {!calendarAdded ? (
              <Button 
                onClick={addToCalendarDirect}
                variant="outline" 
                className="w-full py-4 border-2 border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold"
                data-testid="add-to-calendar-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Add to Google Calendar
              </Button>
            ) : (
              <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-center space-x-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-medium">Calendar event ready to add!</span>
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
            <ul className="text-left space-y-2 text-gray-700">
              <li className="flex items-start">
                <Mail className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>You'll receive a confirmation email shortly</span>
              </li>
              <li className="flex items-start">
                <Phone className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>We'll call you 24 hours before your service date to confirm</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Our team will arrive on time and handle everything</span>
              </li>
            </ul>
          </div>

          <div className="bg-emerald-600 text-white rounded-lg p-6 mb-6">
            <p className="font-semibold mb-2">
              💡 Pro Tip: Save 15% on your service!
            </p>
            <p className="text-emerald-50 text-sm">
              Have all items outside and ready for pickup to receive your discount.
            </p>
          </div>

          <div className="space-y-3">
            <Link to="/my-bookings" className="block">
              <Button variant="outline" className="w-full py-6 text-lg font-semibold border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                <Calendar className="w-5 h-5 mr-2" />
                View My Bookings
              </Button>
            </Link>

            <Link to="/" className="block">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold">
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </Link>
            
            <a href="tel:3653363364" className="block">
              <Button variant="outline" className="w-full py-6 text-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50">
                <Phone className="w-5 h-5 mr-2" />
                Call Us: (365) 336-3364
              </Button>
            </a>
          </div>

          <p className="text-gray-500 text-sm mt-6">
            Questions? Contact us at <a href="mailto:Info.junkio@gmail.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">Info.junkio@gmail.com</a>
          </p>
        </Card>
      </div>
    </div>
  );
};

export default BookingSuccess;
